import React, { useEffect, useMemo } from "react";
import { ScoreUploaderState } from "../../../../state/admin/score-uploader/ScoreUploaderState";
import { ScoreMapping } from "../../../../types/admin/score-uploader/ScoreMapping";
import { ScoreUploaderStep } from "../../../../types/admin/score-uploader/ScoreUploaderStep";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/pro-solid-svg-icons";
import ValidationWarning from "../../../../components/common/ValidationWarning";
import ValidationResult, {
  ValidationError,
} from "../../../../types/forms/ValidationResult";
import { IdentifierColumnType } from "../../../../types/admin/score-uploader/IdentifierColumnType";
import { useAuth } from "react-oidc-context";
import scoreUploadApi from "../../../../api/dashboard/scoreUploadApi";
import { UpdateScoreUploadMappingsRequest } from "../../../../types/admin/score-uploader/UpdateScoreUploadMappingsRequest";
import { IDENTIFIER_TYPE_DISPLAY_NAMES } from "../../../../types/admin/score-uploader/IdentifierTypeDisplayNames";

interface ScoreUploaderColumnMappingProps {
  state: ScoreUploaderState;
  onStateChange: (
    state: ScoreUploaderState | Partial<ScoreUploaderState>
  ) => void;
}

const ScoreUploaderColumnMapping: React.FC<ScoreUploaderColumnMappingProps> = ({
  state,
  onStateChange,
}) => {
  const auth = useAuth();
  const scoreApi = new scoreUploadApi(auth.user?.access_token);

  const [identifierValidation, setIdentifierValidation] =
    React.useState<ValidationResult | null>(null);
  const [mappingsValidation, setMappingsValidation] =
    React.useState<ValidationResult | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Calculate available columns (A-Z, then AA-AZ)
  const allColumns = useMemo(() => {
    const columns: string[] = [];
    // Add single letters A-Z
    for (let i = 0; i < 26; i++) {
      columns.push(String.fromCharCode(65 + i));
    }
    // Add double letters AA-AZ
    for (let i = 0; i < 26; i++) {
      columns.push("A" + String.fromCharCode(65 + i));
    }
    return columns;
  }, []);

  // Calculate which columns are already used
  const usedColumns = useMemo(() => {
    const columns = new Set<string>();
    if (state.identifierColumn) {
      columns.add(state.identifierColumn);
    }
    state.scoreMappings.forEach((mapping) => {
      if (mapping.columnId) {
        columns.add(mapping.columnId);
      }
    });
    return columns;
  }, [state.identifierColumn, state.scoreMappings]);

  // Get available columns (not yet used)
  const availableColumns = useMemo(
    () => allColumns.filter((col) => !usedColumns.has(col)),
    [allColumns, usedColumns]
  );

  // Get available score types for a specific mapping
  const getAvailableScoreTypes = (currentMappingId: string) => {
    if (!state.availableMappings) return [];

    // Get all used score type IDs except for the current mapping
    const usedScoreTypes = state.scoreMappings
      .filter((m) => m.id !== currentMappingId)
      .map((m) => m.dataPointId);

    // Only show score types that aren't used in other mappings
    return state.availableMappings.filter(
      (option) => !usedScoreTypes.includes(option.id.toString())
    );
  };

  // Initialize score mappings from suggested mappings if available
  useEffect(() => {
    if (
      state.uploadedFile &&
      state.suggestedMappings &&
      state.suggestedMappings.length > 0 &&
      state.scoreMappings.length === 0 &&
      !state.hasSuggestedMappingsBeenInitialized
    ) {
      const initialMappings: ScoreMapping[] = state.suggestedMappings.map(
        (mapping) => ({
          id: Math.random().toString(36).substr(2, 9),
          columnId: mapping.column || "",
          dataPointId: mapping.dataPointId.toString() || "",
        })
      );

      onStateChange({
        ...state,
        scoreMappings: initialMappings,
        hasSuggestedMappingsBeenInitialized: true,
      });
    }
  }, [
    state.uploadedFile,
    state.suggestedMappings,
    state.scoreMappings.length,
    state.hasSuggestedMappingsBeenInitialized,
    onStateChange,
    state,
  ]);

  const validateIdentifierSection = (): ValidationResult => {
    const errors: ValidationError[] = [];

    if (!state.identifierColumn || !state.identifierType) {
      errors.push({
        errorType: "REQUIRED",
      });
    }

    return new ValidationResult(errors.length === 0, errors);
  };

  const validateMappingsSection = (): ValidationResult => {
    const errors: ValidationError[] = [];

    // Check if at least one score mapping exists
    if (state.scoreMappings.length === 0) {
      errors.push({
        errorType: "CUSTOM",
        customMessage: "At least one score mapping is required",
      });
      return new ValidationResult(false, errors);
    }

    // Check if all score mappings are complete
    const incompleteMappings = state.scoreMappings.some(
      (m) => !m.columnId || !m.dataPointId
    );
    if (incompleteMappings) {
      errors.push({
        errorType: "CUSTOM",
        customMessage:
          "All score mappings must have both column and score type selected",
      });
    }

    // Check for duplicate column mappings
    const columns = state.scoreMappings.map((m) => m.columnId).filter(Boolean);
    const uniqueColumns = new Set(columns);
    if (columns.length !== uniqueColumns.size) {
      errors.push({
        errorType: "CUSTOM",
        customMessage: "Each column can only be mapped once",
      });
    }

    // Check for duplicate score type mappings
    const scoreTypes = state.scoreMappings
      .map((m) => m.dataPointId)
      .filter(Boolean);
    const uniqueScoreTypes = new Set(scoreTypes);
    if (scoreTypes.length !== uniqueScoreTypes.size) {
      errors.push({
        errorType: "CUSTOM",
        customMessage: "Each score type can only be mapped once",
      });
    }

    // Check if identifier column is used in score mappings
    if (state.identifierColumn && columns.includes(state.identifierColumn)) {
      errors.push({
        errorType: "CUSTOM",
        customMessage: "Identifier column cannot be used as a score column",
      });
    }

    return new ValidationResult(errors.length === 0, errors);
  };

  const handleIdentifierColumnChange = (columnId: string) => {
    onStateChange({
      ...state,
      identifierColumn: columnId,
    });

    // Only clear validation if both fields have values
    if (columnId && state.identifierType) {
      setIdentifierValidation(null);
    }
  };

  const handleIdentifierTypeChange = (type: IdentifierColumnType) => {
    onStateChange({
      ...state,
      identifierType: type,
    });

    // Only clear validation if both fields have values
    if (type && state.identifierColumn) {
      setIdentifierValidation(null);
    }
  };

  const handleAddScoreMapping = () => {
    const newMapping: ScoreMapping = {
      id: Math.random().toString(36).substr(2, 9),
      columnId: "",
      dataPointId: "",
    };
    onStateChange({
      ...state,
      scoreMappings: [...state.scoreMappings, newMapping],
    });
    setMappingsValidation(null);
  };

  const handleRemoveScoreMapping = (mappingId: string) => {
    onStateChange({
      ...state,
      scoreMappings: state.scoreMappings.filter((m) => m.id !== mappingId),
    });
    setMappingsValidation(null);
  };

  const handleScoreMappingChange = (
    mappingId: string,
    field: keyof ScoreMapping,
    value: string
  ) => {
    const updatedMappings = state.scoreMappings.map((m) =>
      m.id === mappingId ? { ...m, [field]: value } : m
    );

    onStateChange({
      ...state,
      scoreMappings: updatedMappings,
    });
    setMappingsValidation(null);
  };

  const handleNext = async () => {
    const identifierResult = validateIdentifierSection();
    const mappingsResult = validateMappingsSection();

    setIdentifierValidation(identifierResult);
    setMappingsValidation(mappingsResult);

    if (
      identifierResult.isValid &&
      mappingsResult.isValid &&
      state.uploadId &&
      state.identifierColumn &&
      state.identifierType
    ) {
      setIsSubmitting(true);

      // Prepare the mapping data
      const mappingData: UpdateScoreUploadMappingsRequest = {
        uploadId: state.uploadId,
        identifier: {
          column: state.identifierColumn,
          type: state.identifierType,
        },
        mappings: state.scoreMappings.map((mapping) => ({
          column: mapping.columnId,
          dataPointId: parseInt(mapping.dataPointId),
        })),
      };

      // Call the API to update mappings
      scoreApi.updateUploadWithMappings(
        mappingData,
        (response) => {
          setIsSubmitting(false);
          if (response.isValid) {
            // If validation is successful, proceed to next step
            onStateChange({
              ...state,
              currentStep: ScoreUploaderStep.Confirm,
            });
          } else {
            // If validation fails, show the errors
            setMappingsValidation(new ValidationResult(false, response.errors));
          }
        },
        (error) => {
          setIsSubmitting(false);
          // Handle any API errors
          setMappingsValidation(
            new ValidationResult(false, [
              {
                errorType: "CUSTOM",
                customMessage:
                  "An error occurred while updating the mappings. Please try again.",
              },
            ])
          );
        }
      );
    }
  };

  const handleBack = () => {
    onStateChange({
      ...state,
      currentStep: ScoreUploaderStep.UploadFile,
      validationResult: null,
      showValidationErrors: false,
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-lg space-y-8">
        {/* Employee Identifier Settings Section */}
        <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-lg font-medium text-gray-900">
              Employee Identifier Settings
            </p>
            <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
              Required
            </span>
          </div>
          <p className="mb-4 text-sm text-gray-500">
            Select the column that contains the employee identifier and specify
            its type. This will be used to match scores with the correct
            employees.
          </p>

          {/* Show validation errors for identifier section */}
          {identifierValidation && !identifierValidation.isValid && (
            <div className="mb-4">
              <ValidationWarning
                isValid={false}
                errors={identifierValidation.errors}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Column
              </label>
              <select
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={state.identifierColumn || ""}
                onChange={(e) => handleIdentifierColumnChange(e.target.value)}
              >
                <option value="">Select column...</option>
                {[...availableColumns, state.identifierColumn]
                  .filter(Boolean)
                  .map((col) => (
                    <option key={col} value={col}>
                      Column {col}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Identifier Type
              </label>
              <select
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={state.identifierType || ""}
                onChange={(e) =>
                  handleIdentifierTypeChange(
                    e.target.value as IdentifierColumnType
                  )
                }
              >
                <option value="">Select identifier type...</option>
                {(
                  Object.entries(IDENTIFIER_TYPE_DISPLAY_NAMES) as [
                    IdentifierColumnType,
                    string
                  ][]
                ).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Score Type Mapping Section */}
        <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-lg font-medium text-gray-900">
              Score Type Mapping
            </p>
            <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
              Required
            </span>
          </div>

          <p className="mb-4 text-sm text-gray-500">
            Map at least one column from your spreadsheet to a score type. You
            can map as many columns as needed, but each score type can only be
            mapped once.
          </p>

          {/* Show validation errors for mappings section */}
          {mappingsValidation && !mappingsValidation.isValid && (
            <div className="mb-4">
              <ValidationWarning
                isValid={false}
                errors={mappingsValidation.errors}
              />
            </div>
          )}

          {/* Column Headers - Only show when mappings exist */}
          {state.scoreMappings.length > 0 && (
            <div className="grid grid-cols-[1fr,1fr,auto] gap-4 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Column
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Score Type
              </label>
              <div className="w-8"></div>
            </div>
          )}

          {/* Mapping Rows */}
          <div className="space-y-3">
            {state.scoreMappings.map((mapping) => (
              <div
                key={mapping.id}
                className="grid grid-cols-[1fr,1fr,auto] gap-4 items-start"
              >
                <div>
                  <select
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={mapping.columnId}
                    onChange={(e) =>
                      handleScoreMappingChange(
                        mapping.id,
                        "columnId",
                        e.target.value
                      )
                    }
                  >
                    <option value="">Select column...</option>
                    {[...availableColumns, mapping.columnId]
                      .filter(Boolean)
                      .map((col) => (
                        <option key={col} value={col}>
                          Column {col}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <select
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={mapping.dataPointId}
                    onChange={(e) =>
                      handleScoreMappingChange(
                        mapping.id,
                        "dataPointId",
                        e.target.value
                      )
                    }
                  >
                    <option value="">Select score type...</option>
                    {getAvailableScoreTypes(mapping.id).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  className="inline-flex items-center p-2 border border-transparent rounded-md text-rose-500 hover:text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500"
                  onClick={() => handleRemoveScoreMapping(mapping.id)}
                  aria-label="Remove this score mapping"
                >
                  <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Persistent Add Row Section */}
            <button
              onClick={handleAddScoreMapping}
              className="w-full grid grid-cols-[auto,1fr] gap-4 items-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-0 transition-colors group"
            >
              <div className="rounded-full bg-gray-100 p-2 group-hover:bg-gray-200 transition-colors">
                <FontAwesomeIcon
                  icon={faPlus}
                  className="h-4 w-4 text-gray-500"
                />
              </div>
              <div className="text-left">
                <span className="text-sm font-medium text-gray-900">
                  Add new column mapping
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          className="btn-secondary"
          onClick={handleBack}
          disabled={isSubmitting}
        >
          Previous
        </button>
        <button
          className="btn-primary"
          onClick={handleNext}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Next"}
        </button>
      </div>
    </div>
  );
};

export default ScoreUploaderColumnMapping;
